import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import { Modal } from '../components/Modal.jsx';
import { SearchableSelect } from './SearchableSelect.jsx';

export function GhlTeamSyncPanel({ ghlConfigured, onFlash }) {
  const [data, setData] = useState({
    users: [],
    team_members: [],
    ghl_configured: false,
    company_id_set: false,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkUser, setLinkUser] = useState(null);
  const [createUser, setCreateUser] = useState(null);
  const [linkUserId, setLinkUserId] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    sendInvite: true,
    role: 'team_member',
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiJson('/api/admin/ghl/users'));
    } catch (e) {
      onFlash?.(e.message, true);
    } finally {
      setLoading(false);
    }
  }, [onFlash]);

  useEffect(() => {
    if (ghlConfigured) {
      void load();
    } else {
      setLoading(false);
    }
  }, [ghlConfigured, load]);

  const unlinkedTeamMembers = (data.team_members || []).filter((member) => !member.ghl_user_id);
  const ready = ghlConfigured;

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await apiJson('/api/admin/ghl/users/sync', { method: 'POST' });
      const stats = res.stats;
      onFlash?.(
        `Team sync done: ${stats.linked} linked by email, ${stats.already_linked} already linked, ${stats.unlinked} still unlinked.`,
      );
      await load();
    } catch (e) {
      onFlash?.(e.message, true);
    } finally {
      setSyncing(false);
    }
  };

  const openLink = (ghlUser) => {
    setLinkUser(ghlUser);
    setLinkUserId(unlinkedTeamMembers[0]?.id ? String(unlinkedTeamMembers[0].id) : '');
  };

  const openCreate = (ghlUser) => {
    setCreateUser(ghlUser);
    setCreateForm({
      name: ghlUser.name || '',
      email: ghlUser.email || '',
      password: '',
      passwordConfirmation: '',
      sendInvite: true,
      role: 'team_member',
    });
  };

  const submitLink = async () => {
    if (!linkUser || !linkUserId) {
      return;
    }
    setBusy(true);
    try {
      await apiJson('/api/admin/ghl/users/link', {
        method: 'PUT',
        body: {
          ghl_user_id: linkUser.ghl_id,
          user_id: Number(linkUserId),
        },
      });
      onFlash?.(`Linked ${linkUser.name} to team member.`);
      setLinkUser(null);
      await load();
    } catch (e) {
      onFlash?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createUser) {
      return;
    }
    setBusy(true);
    try {
      await apiJson('/api/admin/ghl/users/create-team-member', {
        method: 'POST',
        body: createForm.sendInvite
          ? {
              ghl_user_id: createUser.ghl_id,
              name: createForm.name,
              email: createForm.email,
              role: createForm.role,
              send_invite: true,
            }
          : {
              ghl_user_id: createUser.ghl_id,
              name: createForm.name,
              email: createForm.email,
              password: createForm.password,
              password_confirmation: createForm.passwordConfirmation,
              role: createForm.role,
            },
      });
      onFlash?.(`Created team member for ${createUser.name}.`);
      setCreateUser(null);
      await load();
    } catch (e) {
      onFlash?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const unlinkMember = async (member) => {
    if (!window.confirm(`Unlink ${member.name} from Go High Level?`)) {
      return;
    }
    setBusy(true);
    try {
      await apiJson(`/api/admin/ghl/users/link/${member.id}`, { method: 'DELETE' });
      onFlash?.(`Unlinked ${member.name}.`);
      await load();
    } catch (e) {
      onFlash?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  if (!ghlConfigured) {
    return null;
  }

  return (
    <div style={{ marginBottom: '2rem' }} data-tour="ghl-team-sync">
      <h3>Team sync</h3>
      <p className="muted small">
        Match Go High Level users to portal team members. Auto-sync links by email when possible.
      </p>

      <div className="row-actions" style={{ marginBottom: '1rem' }}>
        <span className={`badge ${ready ? 'text-bg-success' : 'text-bg-secondary'}`}>
          GHL users: {ready ? 'ready' : 'GHL not configured'}
        </span>
        <button
          type="button"
          className="btn-secondary"
          disabled={syncing || !ready}
          onClick={() => void runSync()}
        >
          Sync users from GHL
        </button>
        <button type="button" className="btn-link" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {loading ? <p className="muted">Loading…</p> : null}

      {!loading && ready ? (
        <>
          <h4>GHL users</h4>
          {(data.users || []).length === 0 ? (
            <p className="muted">No users found. Check your GHL agency token has users.readonly scope.</p>
          ) : (
            <div className="table-wrap" style={{ marginBottom: '1.5rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Linked team member</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data.users || []).map((ghlUser) => (
                    <tr key={ghlUser.ghl_id}>
                      <td>{ghlUser.name}</td>
                      <td>{ghlUser.email || '—'}</td>
                      <td>
                        {ghlUser.linked_user ? (
                          <>
                            {ghlUser.linked_user.name}
                            <span className="muted small"> ({ghlUser.linked_user.email})</span>
                          </>
                        ) : (
                          <span className="muted">Not linked</span>
                        )}
                      </td>
                      <td className="row-actions">
                        {ghlUser.linked_user ? null : (
                          <>
                            <button
                              type="button"
                              className="btn-link"
                              disabled={busy || unlinkedTeamMembers.length === 0}
                              onClick={() => openLink(ghlUser)}
                            >
                              Link
                            </button>
                            <button type="button" className="btn-link" disabled={busy} onClick={() => openCreate(ghlUser)}>
                              Create new
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>Team members</h4>
          {(data.team_members || []).length === 0 ? (
            <p className="muted">No team members yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>GHL link</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data.team_members || []).map((member) => {
                    const linkedGhlUser = (data.users || []).find(
                      (ghlUser) => ghlUser.linked_user?.id === member.id,
                    );
                    return (
                      <tr key={member.id}>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                        <td>{member.role === 'team_member' ? 'Team member' : 'Team admin'}</td>
                        <td>{linkedGhlUser ? linkedGhlUser.name : member.ghl_user_id || '—'}</td>
                        <td className="row-actions">
                          {member.ghl_user_id ? (
                            <button type="button" className="btn-link" disabled={busy} onClick={() => void unlinkMember(member)}>
                              Unlink
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      <Modal
        open={!!linkUser}
        title={linkUser ? `Link ${linkUser.name}` : 'Link user'}
        description="Associate this GHL user with an existing team member."
        onClose={() => setLinkUser(null)}
      >
        <label className="field">
          <span>Team member</span>
          <SearchableSelect value={linkUserId} onChange={(e) => setLinkUserId(e.target.value)}>
            <option value="">Select team member…</option>
            {unlinkedTeamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} ({member.email})
              </option>
            ))}
          </SearchableSelect>
        </label>
        <div className="row-actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn-secondary" onClick={() => setLinkUser(null)}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy || !linkUserId} onClick={() => void submitLink()}>
            {busy ? 'Saving…' : 'Link'}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!createUser}
        title={createUser ? `Create team member — ${createUser.name}` : 'Create team member'}
        description="Create a new portal team account linked to this GHL user."
        onClose={() => setCreateUser(null)}
      >
        <form onSubmit={submitCreate} className="stack">
          <FormInput
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
          />
          <FormInput
            label="Email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            required
          />
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={createForm.sendInvite}
              onChange={(e) => setCreateForm({ ...createForm, sendInvite: e.target.checked })}
            />
            <span>Send a link so they can create their own password</span>
          </label>
          {!createForm.sendInvite ? (
            <>
              <FormInput
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
              />
              <FormInput
                label="Confirm password"
                type="password"
                value={createForm.passwordConfirmation}
                onChange={(e) => setCreateForm({ ...createForm, passwordConfirmation: e.target.value })}
                required
              />
            </>
          ) : null}
          <label className="field">
            <span>Role</span>
            <SearchableSelect
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            >
              <option value="team_member">Team member</option>
              <option value="manager">Team admin</option>
            </SearchableSelect>
          </label>
          <div className="row-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn-secondary" onClick={() => setCreateUser(null)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
